import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { MapPin, Globe, Calendar, Edit, Users } from 'lucide-react';
import type { User } from '@/types/database';
import { cn } from '@/lib/utils';
import { FollowersList } from './FollowersList';
import { FollowingList } from './FollowingList';

export function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    try {
      // Get profile data with posts count
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          posts:posts(count),
          followers:follows!follows_following_id_fkey(count),
          following:follows!follows_follower_id_fkey(count)
        `)
        .eq('id', id)
        .single();

      if (profileError) throw profileError;
      
      // Transform the counts into the expected format
      const transformedProfile = {
        ...profile,
        posts_count: profile.posts?.[0]?.count || 0,
        followers_count: profile.followers?.[0]?.count || 0,
        following_count: profile.following?.[0]?.count || 0
      };
      
      setUser(transformedProfile);

      // Get posts
      const { data: userPosts, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          user:profiles(*),
          comments:comments(count),
          user_reaction:reactions(*)
        `)
        .eq('user_id', id)
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;
      setPosts(userPosts || []);

      // Check if following
      if (currentUser) {
        const { data: followData, error: followError } = await supabase
          .from('follows')
          .select('*')
          .eq('follower_id', currentUser.id)
          .eq('following_id', id)
          .maybeSingle();

        if (followError) throw followError;
        setIsFollowing(!!followData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('toggle_follow', {
          target_user_uuid: id
        });

      if (error) throw error;
      setIsFollowing(data);
      
      // Update follower count optimistically
      if (user) {
        setUser(prev => prev ? {
          ...prev,
          followers_count: prev.followers_count + (data ? 1 : -1)
        } : null);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error || 'User not found'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Cover image */}
      <div className="h-48 bg-gradient-to-r from-royal-purple/20 to-royal-purple/10 rounded-xl overflow-hidden">
        {user.banner_url && (
          <img
            src={user.banner_url}
            alt="Profile banner"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Profile info */}
      <div className="relative px-6 -mt-16">
        <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4">
          {/* Avatar */}
          <img
            src={user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${user.full_name}`}
            alt={user.full_name}
            className="w-32 h-32 rounded-full ring-4 ring-white dark:ring-dark-paper shadow-lg"
          />

          {/* User info */}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold">{user.full_name}</h1>
            <p className="text-gray-600 dark:text-gray-400">@{user.username}</p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            {currentUser?.id === user.id ? (
              <button
                onClick={() => navigate('/profile/edit')}
                className="px-4 py-2 rounded-lg border-2 border-royal-purple text-royal-purple hover:bg-royal-purple hover:text-white transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <button
                onClick={handleFollow}
                className={cn(
                  "px-6 py-2 rounded-lg font-medium transition-colors group relative",
                  isFollowing
                    ? "bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600"
                    : "bg-royal-purple text-white hover:bg-royal-purple/90"
                )}
              >
                <span className={cn(
                  "block transition-opacity",
                  isFollowing ? "group-hover:hidden" : ""
                )}>
                  {isFollowing ? 'Following' : 'Follow'}
                </span>
                {isFollowing && (
                  <span className="hidden group-hover:block">
                    Unfollow
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Bio and stats */}
        <div className="mt-6 grid gap-6 sm:grid-cols-[2fr,1fr]">
          <div className="space-y-4">
            {user.bio && (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{user.bio}</p>
            )}

            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              {user.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{user.location}</span>
                </div>
              )}
              {user.website && (
                <a
                  href={user.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-royal-purple hover:underline"
                >
                  <Globe className="w-4 h-4" />
                  <span>{new URL(user.website).hostname}</span>
                </a>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>
                  Joined {new Date(user.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-6 text-center">
            <div>
              <p className="text-2xl font-bold">{user.posts_count}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Posts</p>
            </div>
            <button
              onClick={() => setShowFollowers(true)}
              className="hover:bg-gray-50 dark:hover:bg-dark-elevated rounded-lg p-2 transition-colors"
            >
              <p className="text-2xl font-bold">{user.followers_count}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Followers</p>
            </button>
            <button
              onClick={() => setShowFollowing(true)}
              className="hover:bg-gray-50 dark:hover:bg-dark-elevated rounded-lg p-2 transition-colors"
            >
              <p className="text-2xl font-bold">{user.following_count}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Following</p>
            </button>
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="border-t dark:border-dark-border mt-8 pt-8">
        <h2 className="text-xl font-bold mb-6">Posts</h2>
        {posts.length > 0 ? (
          <div className="space-y-6">
            {posts.map((post) => (
              <div
                key={post.id}
                className="bg-white dark:bg-dark-paper rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={post.user?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${post.user?.full_name}`}
                    alt={post.user?.full_name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="font-medium">{post.user?.full_name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(post.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{post.content}</p>

                {post.media_url && (
                  post.media_type === 'video' ? (
                    <video
                      src={post.media_url}
                      controls
                      className="mt-4 rounded-lg max-h-96 w-full"
                    />
                  ) : (
                    <img
                      src={post.media_url}
                      alt="Post media"
                      className="mt-4 rounded-lg max-h-96 w-full object-cover"
                    />
                  )
                )}

                <div className="flex items-center gap-6 mt-4 pt-4 border-t dark:border-dark-border">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Users className="w-5 h-5" />
                    <span>
                      {Object.values(post.reaction_counts || {}).reduce((a, b) => a + b, 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Users className="w-5 h-5" />
                    <span>{post.comments?.length || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No posts yet
          </div>
        )}
      </div>

      {/* Followers/Following modals */}
      {showFollowers && (
        <FollowersList
          userId={user.id}
          onClose={() => setShowFollowers(false)}
        />
      )}
      {showFollowing && (
        <FollowingList
          userId={user.id}
          onClose={() => setShowFollowing(false)}
        />
      )}
    </div>
  );
}